import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useNavigateWithParams from 'libs/design-system/src/hooks/useNavigateWithParams';
import { Download } from '@unified-checkout/design-system/icons/index';
import { Button } from 'libs/design-system/src/components/Button';
import { SkeletonBase } from 'libs/design-system/src/components/SkeletonLoader/SkeletonBase';
import { Breadcrumbs } from 'libs/design-system/src/components/Breadcrumbs';

import type { TTransferFileFormat } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer';
import type { ITransferDetailsHeaderProps } from '@app/dashboard/types/innerLayout/balance_and_transfers/single-transfer/header';
import type { TReportTemplateTypes } from '@app/dashboard/types/shared/modals/reports-template';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { REPORT_FILE_TYPES } from '@app/dashboard/constants/balance_and_transfers/single-transfer';
import { BALANCE_TRANSFERS_EVENTS_VALUES } from '@app/dashboard/constants/tracking_events/balance-and-transfers';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';
import { useNotificationStore } from '@app/dashboard/utils/notifier';
import ReportsTemplate from '@app/dashboard/components/shared/modals/ReportsTemplate';

import { isUAECountry } from '../../../../utils';
import { useTransferActionsTracking } from '../../../../hooks/useTransferActionsTracking';

import styles from './header.module.scss';

const Header = ({
  isLoading,
  actions,
  transferId,
  header,
  transactionCount = 0,
}: ITransferDetailsHeaderProps) => {
  const [isReportsTemplateOpen, setIsReportsTemplateOpen] = useState(false);
  const [selectedFileType, setSelectedFileType] =
    useState<TTransferFileFormat | null>(null);

  const { t } = useTranslation();
  const navigateWithParams = useNavigateWithParams();
  const { show: isNotifyShown, state: notifyState } = useNotificationStore();

  const disableWhenNotifier =
    isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS;

  const {
    clickedReportTypeRef,
    handleDownloadAchDocument,
    handleDownloadTransactionsReport,
    downloadAch,
    exportTransferReport,
  } = useTransferActionsTracking({
    transferId: transferId ?? '',
    header,
    transactionCount,
    entryPoint: BALANCE_TRANSFERS_EVENTS_VALUES.DETAILS,
  });

  const handleOpenReportsModal = (type: TTransferFileFormat) => {
    setIsReportsTemplateOpen(true);
    setSelectedFileType(type);
  };

  const handleChooseReportTemplate = (templateType: TReportTemplateTypes) => {
    handleDownloadTransactionsReport(
      selectedFileType as TTransferFileFormat,
      templateType
    );
  };

  return (
    <div className={styles['header']}>
      <div className={styles['header__breadcrumbs']}>
        {isLoading ? (
          <SkeletonBase height="1.5rem" />
        ) : (
          <Breadcrumbs
            linksProps={[
              {
                text: t('innerLayout.balanceTransfers.transfers.title'),
                onClickHandler: () =>
                  navigateWithParams(INNER_ROUTES.TRANSFERS),
                active: true,
              },
              {
                text: t(
                  'innerLayout.balanceTransfers.singleTransfer.transferId',
                  {
                    id: transferId,
                  }
                ),
              },
            ]}
          />
        )}
      </div>
      <div className={styles['header__content']}>
        <h2 className={styles['header__content-title']}>
          {t('innerLayout.balanceTransfers.transfers.title')}
        </h2>
        <div className={styles['header__content-actions']}>
          <Button
            text={t(
              'innerLayout.balanceTransfers.drawerDetails.actions.downloadPdf'
            )}
            size="md"
            leadingIcon={Download}
            onClickHandler={() => handleOpenReportsModal(REPORT_FILE_TYPES.PDF)}
            variant="outlined"
            loading={
              exportTransferReport.isPending &&
              clickedReportTypeRef.current === REPORT_FILE_TYPES.PDF
            }
            disabled={
              exportTransferReport.isPending || isLoading || disableWhenNotifier
            }
          />
          <Button
            text={t(
              'innerLayout.balanceTransfers.drawerDetails.actions.downloadCsv'
            )}
            size="md"
            leadingIcon={Download}
            onClickHandler={() => handleOpenReportsModal(REPORT_FILE_TYPES.CSV)}
            variant="outlined"
            loading={
              exportTransferReport.isPending &&
              clickedReportTypeRef.current === REPORT_FILE_TYPES.CSV
            }
            disabled={
              isLoading || exportTransferReport.isPending || disableWhenNotifier
            }
          />
          {isUAECountry() && (
            <Button
              text={t(
                'innerLayout.balanceTransfers.drawerDetails.actions.downloadProof'
              )}
              size="md"
              leadingIcon={Download}
              onClickHandler={() =>
                handleDownloadAchDocument(actions.ach_document_url)
              }
              loading={downloadAch.isPending}
              disabled={
                downloadAch.isPending || isLoading || !actions.ach_document_url
              }
            />
          )}
        </div>
        <ReportsTemplate
          isOpen={isReportsTemplateOpen}
          handleClose={() => setIsReportsTemplateOpen(false)}
          handleSubmit={handleChooseReportTemplate}
          isLoading={exportTransferReport.isPending}
        />
      </div>
    </div>
  );
};

export default Header;
